import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { Link } from 'react-router-dom';
import { toggleNav } from '../../utils/navigationToggle';
import DropDown from '../DropDown/DropDown';
import { logoutUser } from '../../actions/authActions';
import { hideAppModal, showAppModal } from '../../actions/appActions';
import { getPermissions } from '../../utils/getPermissions';
import { getTitle, getRoutes } from '../../utils/headerUtils';
import { classNames } from '../../utils/v4/utils';

export function HeaderWithoutRouter(props) {
  const { auth } = props;
  const { user: globalUser } = auth || {};
  const { user: localUser, client } = globalUser || {};
  const { firstname, lastname, avatar = '' } = localUser || {};
  const { name: clientName } = client || {};
  const [showProfileDropDown, setShowProfileDropDown] = useState(false);
  const dropDownTogglerRef = useRef(null);
  const tier =
    {
      0: 'Lite',
      1: 'Pro',
      2: 'Enterprise',
    }[client?.tier] ?? 'Unknown';

  const getBreadcrumbs = (props, routes) => {
    const { auth } = props;
    const p = getPermissions(auth);
    const isLiteTier = !p?.atLeastTierPro;

    return routes.map(({ url, name }, index) => {
      const key = `breadcrumb-fragment-${index}`;
      const isLink = index < routes.length - 1;
      // Campaign breadcrumb is available on the Assignment page but should be disabled on Lite tier
      const isDisabled = isLiteTier && url.startsWith('/campaigns') && index === 1;
      const arrowPointer = (
        <svg
          className="rtl:rotate-180 block w-3 h-3 mx-1 text-gray-400"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 6 10"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 9 4-4-4-4"
          />
        </svg>
      );

      if (isDisabled) {
        return (
          <React.Fragment key={key}>
            {index > 0 && arrowPointer}
            <span
              data-test={`breadcrumb-${index + 1}`}
              className="a text-[12px] text-dark-night font-[700] poppins-extrabold max-w-[300px] truncate"
              key={index}
            >
              {name}
            </span>
          </React.Fragment>
        );
      }

      if (isLink) {
        return (
          <React.Fragment key={key}>
            {index > 0 && arrowPointer}
            <Link
              className="text-[12px] mb-0 !text-sunlight poppins-regular max-w-[300px] truncate"
              data-test={`breadcrumb-${index + 1}`}
              to={url}
            >
              {name}
            </Link>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={key}>
          {index > 0 && arrowPointer}
          <span
            className="text-[12px] !text-dark-night mb-0 font-[700] poppins-extrabold max-w-[300px] truncate"
            data-test={`breadcrumb-${index + 1}`}
          >
            {name}
          </span>
        </React.Fragment>
      );
    });
  };

  const showNotificationHistory = () => {
    const { notificationHistory, showAppModal, hideAppModal } = props;

    return () =>
      showAppModal({
        show: true,
        content: 'NotificationHistory',
        title: 'Notification History',
        data: notificationHistory,
        cancelFn: () => hideAppModal(),
      });
  };

  const logoutTheUser = () => {
    const { history } = props;

    return logoutUser().then(({ url }) => {
      if (url) {
        return history.push(url);
      }

      return history.push('/logout');
    });
  };

  const {
    match: { path },
  } = props;
  const routes = getRoutes(props);
  const currentPage = routes.at(-1)?.name;
  const breadcrumbs =
    currentPage === 'Home' || routes.length === 1 ? (
      <p className="text-[#00243E]  th-hidden xl:!flex font-[400] poppins-regular text-[12px]  mb-0">
        Welcome back, hope you&apos;re having a great day
      </p>
    ) : (
      <ol
        className="flex flex-wrap items-center space-x-1 md:space-x-2 rtl:space-x-reverse !mb-0 w-full"
        data-test="breadcrumbs"
      >
        {getBreadcrumbs(props, routes)}
      </ol>
    );
  const title =
    currentPage === 'Home'
      ? `Hello ${firstname},`
      : getTitle(path.slice(1), routes[routes.length - 1].name);
  const { notificationHistory } = props;

  return (
    <div data-test="header" className="flex justify-between w-full">
      <div className="w-[496px] mt-2 flex-1 flex space-x-2 items-center">
        <button type="button" onClick={toggleNav}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={24}
            height={24}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-menu-2 text-[#00243E] hover:text-[#FF5E00]"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 6l16 0" />
            <path d="M4 12l16 0" />
            <path d="M4 18l16 0" />
          </svg>
        </button>
        <div className="flex flex-col justify-center">
          <p
            className="text-[#00243E] font-[700] poppins-extrabold text-[12px] mb-0"
            data-test="page-title"
          >
            {title}
          </p>

          {breadcrumbs}
        </div>
      </div>

      <div className="flex space-x-2 mt-2  items-center max-h-[49px] xl:max-h-[72px] text-dark-night">
        <button
          data-test="notification-history-button"
          type="button"
          onClick={showNotificationHistory()}
          className={classNames(
            notificationHistory?.length ? '' : 'material-icons w-[34px] h-[34px]',
          )}
        >
          {notificationHistory?.length ? (
            <img
              className="w-[34px] h-[34px]  th-hidden xl:!flex  "
              src="/assets/images/icons/notification_bell_present.svg"
              alt="notification bell"
            />
          ) : (
            'notifications'
          )}
        </button>

        <div className="w-[2px] h-[37px] bg-[#3B6C81]" />
        <div className="w-[34px] h-[34px] cursor-pointer relative">
          <button
            data-test="user-dropdown"
            aria-label="show profile menu"
            type="button"
            ref={dropDownTogglerRef}
            className="w-[34px] h-[34px] relative"
            onClick={() => {
              setShowProfileDropDown(!showProfileDropDown);
            }}
          >
            <img
              alt="user-avatar"
              className="w-[34px] h-[34px] rounded-full"
              src={avatar}
              onError={e => {
                e.currentTarget.src = '/assets/images/icons/avatar.svg';
              }}
            />
          </button>

          <DropDown
            show={showProfileDropDown}
            hide={() => setShowProfileDropDown(false)}
            togglerRef={dropDownTogglerRef}
          >
            <div className="-translate-x-[25px] w-[131px] profile-dropdown">
              <Link
                type="button"
                className="appearance-none w-full h-[40px] bg-white rounded-[15px] poppins-regular text-dark-night flex items-center justify-center mt-[1px]"
                to="/user"
                data-test="user-my-account"
              >
                My Account
              </Link>
              <button
                type="button"
                className="appearance-none w-full h-[40px] bg-white rounded-[15px] poppins-regular text-dark-night "
                onClick={() => logoutTheUser()}
              >
                Log Out
              </button>
              <div className="appearance-none w-full h-[40px] bg-white rounded-[15px] poppins-regular text-dark-night flex items-center justify-center mt-[1px] bg-btn-primary-form tier-text">
                {tier}
              </div>
            </div>
          </DropDown>
        </div>
        <div className="">
          <p className="text-[#00243E] text-[12px] font-[700] poppins-extrabold mt-0 mb-0">
            {firstname} {lastname}
          </p>
          <p className="text-[#00243E] text-[12px]  font-[400] poppins-regular th-hidden xl:!flex mb-0 ">
            {clientName}
          </p>
        </div>
      </div>
    </div>
  );
}

HeaderWithoutRouter.propTypes = {
  auth: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  notificationHistory: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
      id: PropTypes.number.isRequired,
      timestamp: PropTypes.string.isRequired,
    }),
  ).isRequired,
  showAppModal: PropTypes.func.isRequired,
  hideAppModal: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  overwrite: PropTypes.object,
};

const mapDispatchToProps = {
  showAppModal,
  hideAppModal,
};
const mapStateToProps = state => ({
  auth: state.auth,
  notificationHistory: state.notifications.history,
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HeaderWithoutRouter));
